import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SessionContext from '../../contexts/SessionContext';
import TripConnection from '../TripConnection';
import TransitDetails from '../TransitDetails';
import TransitInput from '../TransitInput';
import SloganSG from '../SloganSG';
import PublicUser from '../PublicUser';
import Passengers from '../Passengers';
import Weekdays from '../Weekdays';
import TimeSlots from '../TimeSlots';
import BookingButtons from '../BookingButtons';
import extrasData from '../../../images/imageData/extrasData';
import ProfileModal from '../../modals/ProfileModal';
import ModalButton from '../Buttons/ModalButton';
import TripInformation from '../TripInformation';
import EventInformation from '../EventInformation';

const TripDetails = ({ detailsType = 'dashboard', data, searchOptions }) => {
  const { t } = useTranslation();
  const { sessionData } = useContext(SessionContext);
  const toleranceDistance = 5;
  const istMyTrip = data?.UserGuid === sessionData?.userGuid;

  if (!data) {
    return (
      <div className="TripDetails">
        <div className="TripDetails_Placeholder">
          <SloganSG />
        </div>
      </div>
    );
  }

  return (
    <div className="TripDetails">
      <div className="TripDetails_PublicUser">
        <ModalButton
          modalType="div"
          modalFullContent
          render={<ProfileModal guid={data.UserGuid} />}
        >
          <PublicUser userGuid={data.UserGuid} />
        </ModalButton>
      </div>
      {detailsType === 'search' && !istMyTrip && (
        <>
          <hr className="TripDetails_Separator" />
          <BookingButtons data={data} />
        </>
      )}
      <hr className="TripDetails_Separator" />
      <div className="TripDetails_TripInformation">
        <TripInformation data={data} tripGuid={data.Guid} tripType={data.Typ} />
      </div>
      {data.Guid && data.Typ === 'Offer' && istMyTrip && (
        <>
          <hr className="TripDetails_Separator" />
          <div className="TripDetails_Passengers">
            <Passengers
              tripGuid={data.Guid}
              tripUserGuid={data.UserGuid}
              splitCosts={data.SplitCosts}
              tripCosts={data.TripCosts}
            />
          </div>
        </>
      )}
      {data.Remarks && (
        <>
          <hr className="TripDetails_Separator" />
          <div className="TripDetails_Remarks">
            <h2>{t('tripdetails.remarks')}</h2>
            <p>{`${data.Remarks.replace(/<br \/>/g, '\n')}`}</p>
          </div>
        </>
      )}
      <hr className="TripDetails_Separator" />
      <div className="TripDetails_Trip">
        <h2>{t('tripdetails.headline')}</h2>
        {!data?.IsDirect &&
          Math.round(parseFloat(data.StartDistance), 2) >=
            toleranceDistance && (
            <TransitDetails
              title={t('tripdetails.start.openv.title')}
              startPoint={searchOptions?.dep}
              endPoint={data.MapsStart}
              date={data.CycleOptions === 'regular' ? null : data.Date}
              arrivalTime={
                data.DepartureTime
                  ? data.DepartureTime
                  : data.TimeSlots[0].departureTime
              }
            />
          )}

        <TripConnection
          entry={data}
          searchStart={searchOptions?.dep}
          searchDest={searchOptions?.dest}
          showWaypoints
        />
        {!data?.IsDirect &&
          Math.round(parseFloat(data.EndDistance), 2) >= toleranceDistance && (
            <div>
              <TransitDetails
                title={t('tripdetails.dest.openv.title')}
                startPoint={data.MapsDestination}
                endPoint={searchOptions.dest}
                date={data.CycleOptions === 'regular' ? null : data.Date}
                departureTime={
                  data.ArrivalTime
                    ? data.ArrivalTime
                    : data.TimeSlots[0].arrivalTime
                }
              />
              <br />
            </div>
          )}
        {data.Weekdays && <Weekdays data={data} />}
        {data.TimeSlots && data.TimeSlots.length > 0 && (
          <>
            <br />
            <TimeSlots data={data} />
          </>
        )}
        {data?.IsDirect && data.Typ !== 'Request' && (
          <>
            <br />
            <hr className="TripDetails_Separator" />
            <br />
            <TransitInput
              startPoint={searchOptions?.dep}
              arrivalTime={
                data.DepartureTime
                  ? data.DepartureTime
                  : data.TimeSlots[0].departureTime
              }
            />
          </>
        )}
      </div>
      {data.EventId && (
        <>
          <hr className="TripDetails_Separator" />
          <div className="TripDetails_TripInformation">
            <EventInformation eventId={data.EventId} />
          </div>
        </>
      )}
    </div>
  );
};

TripDetails.propTypes = {
  data: PropTypes.shape({
    Guid: PropTypes.string.isRequired,
    Typ: PropTypes.string.isRequired,
    CarType: PropTypes.string,
    CycleOptions: PropTypes.string,
    FreeSeats: PropTypes.string,
    Fuel: PropTypes.string,
    Company: PropTypes.string,
    Gender: PropTypes.string,
    Luggage: PropTypes.string,
    Radius: PropTypes.string,
    Remarks: PropTypes.string,
    ArrivalTime: PropTypes.string,
    Date: PropTypes.string,
    DepartureTime: PropTypes.string,
    Title: PropTypes.string,
    WeekdayCycle: PropTypes.string,
    ProfileImage: PropTypes.string,
    Weekdays: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ).isRequired,
    ArrivalTimeDifference: PropTypes.number,
    IsDirect: PropTypes.string,
    StartDistance: PropTypes.number,
    EndDistance: PropTypes.number,
    StartLatitude: PropTypes.string,
    StartLongitude: PropTypes.string,
    EndLatitude: PropTypes.string,
    EndLongitude: PropTypes.string,
    Points: PropTypes.number,
    UserGuid: PropTypes.string,
    Firstname: PropTypes.string,
    Lastname: PropTypes.string,
    Username: PropTypes.string,
    Sex: PropTypes.string,
    Conversations: PropTypes.string,
    Smoking: PropTypes.string,
    Music: PropTypes.string,
    Birthdate: PropTypes.string,
    MapsStart: PropTypes.shape({
      Location: PropTypes.string,
      lat: PropTypes.string,
      lng: PropTypes.string,
    }),
    MapsDestination: PropTypes.shape({
      Location: PropTypes.string,
      lat: PropTypes.string,
      lng: PropTypes.string,
    }),
    TimeSlots: PropTypes.arrayOf(
      PropTypes.shape({
        day: PropTypes.string,
        departureTime: PropTypes.string,
        arrivalTime: PropTypes.string,
      }),
    ).isRequired,
    MapsWaypoints: PropTypes.arrayOf(
      PropTypes.shape({
        location: PropTypes.string,
        stopover: PropTypes.bool,
      }),
    ).isRequired,
    Extras: PropTypes.shape({
      selectedOptions: PropTypes.arrayOf(PropTypes.string),
      extrasNumber: PropTypes.shape(extrasData.extrasOptions),
    }),
    EventId: PropTypes.string,
    SplitCosts: PropTypes.oneOf(['split', 'fix', 'free']),
    TripCosts: PropTypes.string,
  }),
  searchOptions: PropTypes.shape({
    /** Departure: dep "Street Number, City, Country" */
    dep: PropTypes.string,
    /** Destination: dest  "Street Number, City, Country" */
    dest: PropTypes.string,
    /** Departure Geo Coordinates: depg "Longitude,Latitude" */
    depg: PropTypes.string,
    /** Destination Geo Coordinates: destg "Longitude,Latitude" */
    destg: PropTypes.string,
    /** Week Days: days "0" for Sunday and "6" for Saturday (Mo. - Fr. => "12345") */
    days: PropTypes.string,
    /** Departure Date: depdate "DD.MM.YYYY" */
    depdate: PropTypes.string,
    /** Departure Time: depdate "hh:mm" */
    deptime: PropTypes.string,
    /** Radius in km: radius "5" */
    radius: PropTypes.string,
    /** Type of search: regular or single */
    type: PropTypes.string,
  }),
  detailsType: PropTypes.oneOf(['search', 'dashboard']).isRequired,
};

export default TripDetails;
